/**
 * Deal Card Component
 * Compact card for displaying deal summary in pipeline view
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ArrowRight } from '@phosphor-icons/react';
import { Badge } from '../ui/badge';
import { 
  formatCurrency, 
  formatPercent, 
  getStatusConfig,
  getRoiColorClass,
  getSourceTypeConfig,
} from '../../utils/formatters';
import { cn } from '../../lib/utils';

export const DealCard = ({ deal, className }) => {
  const statusConfig = getStatusConfig(deal.status);
  const sourceConfig = getSourceTypeConfig(deal.source_type);
  const roiColor = getRoiColorClass(deal.expected_roi_percent);

  return (
    <Link 
      to={`/deal/${deal.id}`}
      data-testid={`deal-card-${deal.id}`}
      className={cn(
        'block card-premium hover:border-primary/30 transition-all group',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-foreground truncate group-hover:text-primary transition-colors">
            {deal.deal_name}
          </h3>
          <div className="flex items-center gap-1 text-sm text-muted-foreground mt-0.5">
            <MapPin size={14} />
            <span className="truncate">{deal.address}</span>
          </div>
        </div>
        <Badge 
          variant="secondary" 
          className={cn('ml-2 flex-shrink-0', statusConfig.color)}
        >
          {statusConfig.label}
        </Badge>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-0.5">
            Coût du Projet
          </p>
          <p className="font-semibold font-tabular">
            {formatCurrency(deal.total_project_cost)}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-0.5">
            ROI Attendu
          </p>
          <p className={cn('font-semibold font-tabular', roiColor)}>
            {formatPercent(deal.expected_roi_percent)}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-border">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {deal.arrondissement} arr.
          </span>
          <span className="text-xs text-muted-foreground">•</span>
          <span className="text-xs text-muted-foreground">
            {deal.surface_sqm} m²
          </span>
        </div>
        <div className="flex items-center gap-1 text-xs text-muted-foreground group-hover:text-primary transition-colors">
          <span>Voir</span>
          <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
        </div>
      </div>
    </Link>
  );
};

export default DealCard;
